import { useState, useEffect, useCallback } from 'react';
import {
  Trophy, CalendarOff, Plane, SlidersHorizontal, AlertOctagon, Plus, Trash2, Save,
  ShieldCheck, UserPlus, UserMinus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { C, fontSerif, fontMono, todayStr, fmtDateShort, currencySymbol } from './utils';
import { incentivesAPI } from '../../lib/api';

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div className="rounded-2xl border bg-white p-6" style={{ borderColor: C.border }} data-testid={testId}>
    <div className="mb-4 flex items-center gap-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: C.muted }}>
        <Icon className="h-4 w-4" style={{ color: C.burgundy }} />
      </div>
      <div>
        <h3 className="text-lg font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>{title}</h3>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

export const AdminSettings = ({ employees = [], settings, onChanged, isCoreAdmin, access }) => {
  const [rw, setRw] = useState({
    reward_threshold: 85, reward_amount: 5000, reward_name: 'Gift Card', almost_there_margin: 5,
  });
  const [savingRw, setSavingRw] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', note: '' });
  const [leaves, setLeaves] = useState([]);
  const [newLeave, setNewLeave] = useState({ employee_id: '', date: '', reason: '' });
  const [kpiEmp, setKpiEmp] = useState('');
  const [assignment, setAssignment] = useState(null);
  const [incident, setIncident] = useState({ employee_id: '', category: '', description: '', severity: 'MEDIUM', reward_impact: 'WARNING' });
  const [newAdmin, setNewAdmin] = useState('');

  const sym = currencySymbol(settings?.currency);
  const adminEmails = access?.admin_emails || [];
  const candidates = access?.candidates || [];
  const candidateName = (email) => (candidates.find((c) => c.email === email)?.name) || email;
  const availableCandidates = candidates.filter(
    (c) => !adminEmails.map((e) => e.toLowerCase()).includes(c.email.toLowerCase()));

  const addAdmin = async () => {
    if (!newAdmin) { toast.error('Pick a teammate'); return; }
    try {
      await incentivesAPI.addAdmin(newAdmin);
      toast.success('Admin added');
      setNewAdmin('');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeAdmin = async (email) => {
    try {
      await incentivesAPI.removeAdmin(email);
      toast.success('Admin removed');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  useEffect(() => {
    if (settings) {
      setRw({
        reward_threshold: settings.reward_threshold ?? 85,
        reward_amount: settings.reward_amount ?? 5000,
        reward_name: settings.reward_name ?? 'Gift Card',
        almost_there_margin: settings.almost_there_margin ?? 5,
      });
    }
  }, [settings]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await incentivesAPI.calendar();
      setHolidays(res.data.holidays || []);
    } catch { /* noop */ }
  }, []);

  const loadLeaves = useCallback(async () => {
    try {
      const res = await incentivesAPI.listLeave();
      setLeaves(res.data || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadCalendar(); loadLeaves(); }, [loadCalendar, loadLeaves]);

  useEffect(() => {
    if (!kpiEmp) { setAssignment(null); return; }
    incentivesAPI.employee(kpiEmp).then((res) => {
      setAssignment({ metrics: res.data.metrics, daily_standard: res.data.employee.daily_standard });
    }).catch(() => setAssignment(null));
  }, [kpiEmp]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || id;

  const saveRewards = async () => {
    setSavingRw(true);
    try {
      await incentivesAPI.updateSettings({
        reward_threshold: Number(rw.reward_threshold),
        reward_amount: Number(rw.reward_amount),
        reward_name: rw.reward_name,
        almost_there_margin: Number(rw.almost_there_margin),
        reason: 'Admin settings update',
      });
      toast.success('Reward settings saved');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingRw(false);
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.date) { toast.error('Pick a date'); return; }
    try {
      await incentivesAPI.addHoliday(newHoliday);
      toast.success('Holiday added');
      setNewHoliday({ date: '', note: '' });
      loadCalendar();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeHoliday = async (date) => {
    try { await incentivesAPI.removeHoliday(date); loadCalendar(); onChanged?.(); }
    catch { toast.error('Failed'); }
  };

  const addLeave = async () => {
    if (!newLeave.employee_id || !newLeave.date) { toast.error('Pick teammate and date'); return; }
    try {
      await incentivesAPI.addLeave(newLeave);
      toast.success('Leave approved');
      setNewLeave({ employee_id: '', date: '', reason: '' });
      loadLeaves();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeLeave = async (id) => {
    try { await incentivesAPI.removeLeave(id); loadLeaves(); onChanged?.(); }
    catch { toast.error('Failed'); }
  };

  const saveMetric = async (metricKey, changes) => {
    try {
      await incentivesAPI.editMetric(kpiEmp, {
        apply_to: 'CURRENT', metric_key: metricKey, changes,
        reason: 'Admin KPI adjustment',
      });
      toast.success('KPI updated for current period');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addIncident = async () => {
    if (!incident.employee_id) { toast.error('Pick a teammate'); return; }
    try {
      await incentivesAPI.addIncident(incident);
      toast.success('Incident logged');
      setIncident({ employee_id: '', category: '', description: '', severity: 'MEDIUM', reward_impact: 'WARNING' });
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-kpi-settings">
      {/* Incentive Admins (founder only) */}
      {isCoreAdmin && (
        <Section icon={ShieldCheck} title="Incentive Admins" subtitle="Who can open the Admin Console (Daily Update + Configuration). The founder is always an admin." testId="admin-admins-section">
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg bg-amber-50/60 px-3 py-2 text-sm border border-amber-100">
              <span><span className="font-medium">{access?.founder_email}</span> <span className="text-xs text-slate-400">· founder</span></span>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200">always admin</span>
            </div>
            {adminEmails.length === 0 && (
              <p className="text-xs text-slate-400">No additional admins — only the founder has admin access to this tab.</p>
            )}
            {adminEmails.map((email) => (
              <div key={email} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`admin-row-${email}`}>
                <span><span className="font-medium">{candidateName(email)}</span> <span className="text-xs text-slate-400">· {email}</span></span>
                <button
                  onClick={() => removeAdmin(email)}
                  className="inline-flex items-center gap-1 text-slate-400 transition hover:text-rose-500"
                  data-testid={`remove-admin-${email}`}
                >
                  <UserMinus className="h-4 w-4" /> Remove
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label="Grant admin to">
              <Select value={newAdmin} onValueChange={setNewAdmin}>
                <SelectTrigger className="w-72" data-testid="add-admin-select"><SelectValue placeholder="Select a teammate" /></SelectTrigger>
                <SelectContent>
                  {availableCandidates.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400">Everyone is already an admin</div>
                  ) : availableCandidates.map((c) => (
                    <SelectItem key={c.email} value={c.email}>{c.name} · {c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button onClick={addAdmin} variant="outline" data-testid="add-admin-btn"><UserPlus className="mr-1.5 h-4 w-4" /> Add admin</Button>
          </div>
        </Section>
      )}

      {/* Rewards */}
      <Section icon={Trophy} title="Reward & Threshold" subtitle="Applies to the current open period." testId="admin-rewards-section">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Field label="Unlock threshold">
            <Input type="number" value={rw.reward_threshold} onChange={(e) => setRw({ ...rw, reward_threshold: e.target.value })} style={{ fontFamily: fontMono }} data-testid="reward-threshold-input" />
          </Field>
          <Field label={`Amount (${sym || settings?.currency})`}>
            <Input type="number" value={rw.reward_amount} onChange={(e) => setRw({ ...rw, reward_amount: e.target.value })} style={{ fontFamily: fontMono }} data-testid="reward-amount-input" />
          </Field>
          <Field label="Reward name">
            <Input value={rw.reward_name} onChange={(e) => setRw({ ...rw, reward_name: e.target.value })} data-testid="reward-name-input" />
          </Field>
          <Field label="Almost-there margin">
            <Input type="number" value={rw.almost_there_margin} onChange={(e) => setRw({ ...rw, almost_there_margin: e.target.value })} style={{ fontFamily: fontMono }} data-testid="reward-margin-input" />
          </Field>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveRewards} disabled={savingRw} className="text-white" style={{ background: C.burgundy }} data-testid="save-kpi-config">
            <Save className="mr-1.5 h-4 w-4" /> {savingRw ? 'Saving…' : 'Save rewards'}
          </Button>
        </div>
      </Section>

      {/* KPI editor */}
      <Section icon={SlidersHorizontal} title="KPI Weights & Targets" subtitle="Tune a teammate's KPIs for the current period." testId="admin-kpi-editor">
        <Select value={kpiEmp} onValueChange={setKpiEmp}>
          <SelectTrigger className="w-64" data-testid="kpi-employee-select"><SelectValue placeholder="Select teammate" /></SelectTrigger>
          <SelectContent>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.role_title}</SelectItem>)}
          </SelectContent>
        </Select>

        {assignment && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: C.border }}>
              <span className="text-sm font-medium text-slate-600">Daily standard</span>
              <Input
                type="number" defaultValue={assignment.daily_standard}
                className="h-9 w-24" style={{ fontFamily: fontMono }}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== assignment.daily_standard) saveMetric(null, { daily_standard: v }); }}
                data-testid="kpi-daily-standard-input"
              />
              <span className="text-xs text-slate-400">integer units/day</span>
            </div>
            {assignment.metrics.map((m) => (
              <div key={m.key} className="flex flex-wrap items-center gap-3 rounded-xl border p-3" style={{ borderColor: C.border }} data-testid={`kpi-edit-${m.key}`}>
                <span className="min-w-[180px] flex-1 text-sm font-medium" style={{ color: C.text }}>{m.label}</span>
                <label className="flex items-center gap-1.5 text-xs text-slate-500">
                  Weight
                  <Input type="number" defaultValue={m.weight} className="h-8 w-20" style={{ fontFamily: fontMono }}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== m.weight) saveMetric(m.key, { weight: v }); }}
                    data-testid={`kpi-weight-slider-${m.key}`} />
                </label>
                {m.target_rate != null && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    Target %
                    <Input type="number" defaultValue={m.target_rate} className="h-8 w-20" style={{ fontFamily: fontMono }}
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== m.target_rate) saveMetric(m.key, { target_rate: v }); }}
                      data-testid={`kpi-target-${m.key}`} />
                  </label>
                )}
                {m.gate != null && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    Gate %
                    <Input type="number" defaultValue={m.gate} className="h-8 w-20" style={{ fontFamily: fontMono }}
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== m.gate) saveMetric(m.key, { critical_gate: v }); }}
                      data-testid={`kpi-gate-${m.key}`} />
                  </label>
                )}
              </div>
            ))}
            <p className="text-xs text-slate-400">Changes save on blur and are logged in the audit trail.</p>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Holidays */}
        <Section icon={CalendarOff} title="Company Holidays" subtitle="Excluded from working days for everyone." testId="admin-holidays-section">
          <div className="flex items-end gap-2">
            <Field label="Date"><Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} data-testid="holiday-date-input" /></Field>
            <Field label="Note"><Input value={newHoliday.note} onChange={(e) => setNewHoliday({ ...newHoliday, note: e.target.value })} placeholder="Diwali…" data-testid="holiday-note-input" /></Field>
            <Button onClick={addHoliday} variant="outline" data-testid="add-holiday-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 space-y-2">
            {holidays.length === 0 && <p className="text-xs text-slate-400">No holidays added.</p>}
            {holidays.map((h) => (
              <div key={h.date} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span><span className="font-medium" style={{ fontFamily: fontMono }}>{fmtDateShort(h.date)}</span> {h.note && <span className="text-slate-400">· {h.note}</span>}</span>
                <button onClick={() => removeHoliday(h.date)} className="text-slate-400 hover:text-rose-500" data-testid={`remove-holiday-${h.date}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </Section>

        {/* Leaves */}
        <Section icon={Plane} title="Approved Leave" subtitle="Leave days are excluded from that teammate's targets." testId="admin-leaves-section">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Teammate">
              <Select value={newLeave.employee_id} onValueChange={(v) => setNewLeave({ ...newLeave, employee_id: v })}>
                <SelectTrigger className="w-40" data-testid="leave-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date"><Input type="date" value={newLeave.date} onChange={(e) => setNewLeave({ ...newLeave, date: e.target.value })} data-testid="leave-date-input" /></Field>
            <Button onClick={addLeave} variant="outline" data-testid="add-leave-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
            {leaves.length === 0 && <p className="text-xs text-slate-400">No leave recorded.</p>}
            {leaves.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span><span className="font-medium">{empName(l.employee_id)}</span> · <span style={{ fontFamily: fontMono }}>{fmtDateShort(l.date)}</span></span>
                <button onClick={() => removeLeave(l.id)} className="text-slate-400 hover:text-rose-500" data-testid={`remove-leave-${l.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Incidents */}
      <Section icon={AlertOctagon} title="Critical Incident" subtitle="Log a quality/behaviour incident that can block or reduce the reward." testId="admin-incidents-section">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Teammate">
            <Select value={incident.employee_id} onValueChange={(v) => setIncident({ ...incident, employee_id: v })}>
              <SelectTrigger data-testid="incident-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category"><Input value={incident.category} onChange={(e) => setIncident({ ...incident, category: e.target.value })} placeholder="e.g. Data accuracy" data-testid="incident-category-input" /></Field>
          <Field label="Severity">
            <Select value={incident.severity} onValueChange={(v) => setIncident({ ...incident, severity: v })}>
              <SelectTrigger data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
              <SelectContent>{['LOW', 'MEDIUM', 'HIGH'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reward impact">
            <Select value={incident.reward_impact} onValueChange={(v) => setIncident({ ...incident, reward_impact: v })}>
              <SelectTrigger data-testid="incident-impact-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WARNING">Warning only</SelectItem>
                <SelectItem value="TEMPORARY_BLOCK">Temporary block</SelectItem>
                <SelectItem value="DISQUALIFY_PERIOD">Disqualify period</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Textarea value={incident.description} onChange={(e) => setIncident({ ...incident, description: e.target.value })} placeholder="What happened?" rows={2} className="mt-4" data-testid="incident-description-input" />
        <div className="mt-3 flex justify-end">
          <Button onClick={addIncident} variant="outline" data-testid="add-incident-btn"><Plus className="mr-1.5 h-4 w-4" /> Log incident</Button>
        </div>
      </Section>
    </div>
  );
};
